import {
  ArrowDownIcon,
  ArrowUpIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import React, { useState } from 'react';

import { RemovableDashedTag } from '@/components/RemovableDashedTag';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { HuntScopeStep } from '@/sections/insights/aiAssistant/components/HuntScopeStep';
import { HuntScopeTabs } from '@/sections/insights/aiAssistant/components/HuntScopeTabs';
import {
  HUNT_AGENT_OPTIONS,
  type HuntScheduleConfig,
  type HuntScheduleEntry,
  newEntry,
} from '@/sections/settings/components/forms/huntScheduleConstants';
import { cn } from '@/utils/classname';

interface HuntScheduleStepProps {
  config: HuntScheduleConfig;
  onChange: (config: HuntScheduleConfig) => void;
  disabled?: boolean;
}

function agentKind(
  agent: string
): 'external' | 'cloud' | 'webapplication' | 'llm' {
  switch (agent) {
    case 'hannibal-cloud':
      return 'cloud';
    case 'hannibal-webapp':
      return 'webapplication';
    case 'hannibal-llm':
      return 'llm';
    default:
      return 'external';
  }
}

function formatRelativeTime(iso: string): string {
  if (!iso) return 'Never';
  const d = new Date(iso);
  const now = Date.now();
  const diffMs = now - d.getTime();
  if (diffMs < 0) return 'Never';
  const hours = Math.floor(diffMs / 3_600_000);
  if (hours < 1) return 'Just now';
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function ScopePicker({
  agent,
  scope,
  onChange,
}: {
  agent: string;
  scope: string[];
  onChange: (scope: string[]) => void;
}) {
  const kind = agentKind(agent);

  if (kind === 'external') {
    return <HuntScopeTabs value={scope} onChange={onChange} />;
  }

  return (
    <>
      <div className="flex items-center justify-end">
        <span
          aria-live="polite"
          className="text-chariot-text-secondary shrink-0 text-xs"
        >
          {scope.length} selected
        </span>
      </div>
      <div className="flex min-h-0 flex-1 flex-col">
        <HuntScopeStep value={scope} onChange={onChange} kind={kind} />
      </div>
    </>
  );
}

export function HuntScheduleStep({
  config,
  onChange,
  disabled,
}: HuntScheduleStepProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const updateEntry = (id: string, patch: Partial<HuntScheduleEntry>) =>
    onChange({
      ...config,
      entries: config.entries.map(e => (e.id === id ? { ...e, ...patch } : e)),
    });

  const removeEntry = (id: string) =>
    onChange({
      ...config,
      entries: config.entries.filter(e => e.id !== id),
    });

  const addEntry = () => {
    const entry = newEntry();
    entry.name = `Hunt ${config.entries.length + 1}`;
    onChange({ ...config, entries: [...config.entries, entry] });
    setExpandedId(entry.id);
  };

  const moveEntry = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= config.entries.length) return;
    const next = [...config.entries];
    [next[index], next[target]] = [next[target], next[index]];
    onChange({ ...config, entries: next });
  };

  return (
    <div className="flex h-full min-h-0 flex-col gap-4">
      <div className="border-chariot-stroke bg-chariot-background-secondary flex items-center justify-between gap-4 rounded-lg border px-4 py-3">
        <div className="flex flex-col gap-1">
          <label
            htmlFor="hunt-schedule-enabled"
            className="text-chariot-text-primary text-sm font-medium"
          >
            Automatic Scoped Hunts
          </label>
          <span className="text-chariot-text-secondary text-xs">
            Configure hunt profiles that rotate automatically when budget
            headroom is available. Without any profiles, automatic hunts run
            account-wide with the default mandate.
          </span>
        </div>
        <Switch
          id="hunt-schedule-enabled"
          checked={config.enabled}
          onCheckedChange={checked => onChange({ ...config, enabled: checked })}
          disabled={disabled}
          data-testid="hunt-schedule-enabled"
        />
      </div>

      {config.enabled && (
        <>
          <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto">
            {config.entries.length === 0 && (
              <p className="text-chariot-text-secondary py-8 text-center text-sm">
                No scoped hunt profiles configured. Automatic hunts will run
                account-wide. Add a profile to target specific assets.
              </p>
            )}

            {config.entries.map((entry, index) => {
              const expanded = expandedId === entry.id;
              const agentLabel =
                HUNT_AGENT_OPTIONS.find(o => o.value === entry.agent)?.label ??
                entry.agent;

              return (
                <div
                  key={entry.id}
                  className="border-chariot-stroke rounded-lg border"
                  data-testid={`hunt-entry-${entry.id}`}
                >
                  <button
                    type="button"
                    className="flex w-full items-center gap-3 px-4 py-3 text-left"
                    onClick={() => setExpandedId(expanded ? null : entry.id)}
                    disabled={disabled}
                  >
                    <span className="text-chariot-text-secondary text-xs font-medium tabular-nums">
                      {index + 1}
                    </span>
                    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <span className="text-chariot-text-primary truncate text-sm font-medium">
                        {entry.name || 'Untitled'}
                      </span>
                      <span className="text-chariot-text-secondary truncate text-xs">
                        {agentLabel}
                        {entry.scope.length > 0
                          ? ` · ${entry.scope.length} target${entry.scope.length !== 1 ? 's' : ''}`
                          : ' · Account-wide'}
                      </span>
                    </div>
                    <span className="text-chariot-text-secondary shrink-0 text-xs">
                      {formatRelativeTime(entry.last_run_at)}
                    </span>
                    {expanded ? (
                      <ChevronUpIcon className="text-chariot-text-secondary size-4 shrink-0" />
                    ) : (
                      <ChevronDownIcon className="text-chariot-text-secondary size-4 shrink-0" />
                    )}
                  </button>

                  {expanded && (
                    <div className="border-chariot-stroke flex flex-col gap-3 border-t px-4 py-3">
                      <Input
                        placeholder="Profile name"
                        value={entry.name}
                        onChange={e =>
                          updateEntry(entry.id, { name: e.target.value })
                        }
                        disabled={disabled}
                        data-testid="hunt-entry-name"
                      />

                      <div className="flex items-center gap-3">
                        <label className="text-chariot-text-secondary shrink-0 text-sm">
                          Hunt type
                        </label>
                        <Select
                          value={entry.agent || 'hannibal'}
                          onValueChange={agent =>
                            updateEntry(entry.id, { agent, scope: [] })
                          }
                          disabled={disabled}
                        >
                          <SelectTrigger
                            className="w-56"
                            data-testid="hunt-entry-agent"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {HUNT_AGENT_OPTIONS.map(opt => (
                              <SelectItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="flex flex-col gap-1">
                        <label className="text-chariot-text-secondary text-sm">
                          Target scope
                        </label>
                        {entry.scope.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 pb-2">
                            {entry.scope.map(key => (
                              <RemovableDashedTag
                                key={key}
                                label={key.replace(/^#asset#/, '')}
                                size="xs"
                                onRemove={() =>
                                  updateEntry(entry.id, {
                                    scope: entry.scope.filter(s => s !== key),
                                  })
                                }
                              />
                            ))}
                          </div>
                        )}
                        <div
                          className={cn(
                            'flex flex-col',
                            entry.scope.length > 0 ? 'h-48' : 'h-64'
                          )}
                        >
                          <ScopePicker
                            agent={entry.agent}
                            scope={entry.scope}
                            onChange={scope => updateEntry(entry.id, { scope })}
                          />
                        </div>
                      </div>

                      <div className="flex flex-col gap-1">
                        <label className="text-chariot-text-secondary text-sm">
                          Custom prompt (optional)
                        </label>
                        <Textarea
                          rows={2}
                          placeholder="Override the default hunt mandate..."
                          value={entry.prompt}
                          onChange={e =>
                            updateEntry(entry.id, { prompt: e.target.value })
                          }
                          disabled={disabled}
                          data-testid="hunt-entry-prompt"
                        />
                      </div>

                      <div className="flex items-center justify-between pt-1">
                        <div className="flex gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => moveEntry(index, -1)}
                            disabled={disabled || index === 0}
                            aria-label="Move up"
                          >
                            <ArrowUpIcon className="size-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => moveEntry(index, 1)}
                            disabled={
                              disabled || index === config.entries.length - 1
                            }
                            aria-label="Move down"
                          >
                            <ArrowDownIcon className="size-4" />
                          </Button>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className={cn(
                            'text-chariot-stroke-critical hover:text-chariot-stroke-critical'
                          )}
                          onClick={() => removeEntry(entry.id)}
                          disabled={disabled}
                          aria-label="Remove profile"
                          data-testid="hunt-entry-remove"
                        >
                          <TrashIcon className="size-4" />
                          <span className="ml-1">Remove</span>
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <Button
            type="button"
            variant="outline"
            size="sm"
            className="shrink-0 self-start"
            onClick={addEntry}
            disabled={disabled}
            data-testid="hunt-schedule-add-entry"
          >
            <PlusIcon className="mr-1 size-4" />
            Add hunt profile
          </Button>
        </>
      )}
    </div>
  );
}
